/**
 * Gjenbrukbar funksjon for å generere PDF for sanger
 * Brukes både i TranspositionsTab og i produksjonsplan-visning
 */

import { parseText, ParsedLine } from './leadsheetParser';
import { generateLeadsheetPDF } from './pdfGenerator';

/**
 * Transpose a single chord
 */
function transposeChord(chord: string, semitones: number): string {
  if (semitones === 0) return chord;

  const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const noteMatch = chord.match(/^([A-G][#b]?)(.*)/);

  if (!noteMatch) return chord;

  let [, root, suffix] = noteMatch;

  // Normalize flats to sharps
  root = root
    .replace('Db', 'C#')
    .replace('Eb', 'D#')
    .replace('Gb', 'F#')
    .replace('Ab', 'G#')
    .replace('Bb', 'A#');

  let index = notes.indexOf(root);
  if (index === -1) return chord;

  index = (index + semitones + 12) % 12;
  return notes[index] + suffix;
}

/**
 * Fjern akkorder fra tekst for ren tekst-generering
 * Håndterer både inline [C] og chord-line format
 */
function removeChordsFromText(text: string): string {
  const lines = text.split('\n');
  const result: string[] = [];
  
  // Pattern for å identifisere chord lines (akkorder på egen linje)
  const chordPattern = /^[A-G][b#]?(\d+)?(m|maj|min|dim|aug|sus\d*)?(\d+)?(\/[A-G][b#]?)?$/;
  
  let i = 0;
  while (i < lines.length) {
    const currentLine = lines[i];
    
    // Skip empty lines
    if (!currentLine.trim()) {
      result.push('');
      i++;
      continue;
    }
    
    // Check if it's a section marker
    const trimmed = currentLine.trim();
    const isSection = /^\[[\w\s-]+\]$/.test(trimmed) || 
      /^(intro|verse|chorus|bridge|outro|pre-chorus|refrain|tag|coda)\s*\d*$/i.test(trimmed);
    
    if (isSection) {
      result.push(currentLine.replace(/^\[|\]$/g, ''));
      i++;
      continue;
    }
    
    // Check if current line is a chord line
    const tokens = currentLine.trim().split(/\s+/);
    const chordCount = tokens.filter(token => chordPattern.test(token)).length;
    const isChordLine = chordCount >= Math.max(1, tokens.length * 0.5);
    
    if (isChordLine) {
      // Look ahead to find the next non-empty line (skip empty lines)
      let nextLineIndex = i + 1;
      let nextLine = '';
      
      while (nextLineIndex < lines.length) {
        const candidateLine = lines[nextLineIndex];
        if (candidateLine.trim()) {
          nextLine = candidateLine;
          break;
        }
        nextLineIndex++;
      }
      
      // Check if next line is also a chord line or section
      const nextIsChordOrSection = !nextLine || 
        nextLine.trim().split(/\s+/).filter(token => chordPattern.test(token)).length >= Math.max(1, nextLine.trim().split(/\s+/).length * 0.5) ||
        /^\[[\w\s-]+\]$/.test(nextLine.trim()) ||
        /^(intro|verse|chorus|bridge|outro|pre-chorus|refrain|tag|coda)\s*\d*$/i.test(nextLine.trim());
      
      if (nextIsChordOrSection) {
        // Chord line without lyrics - skip it
        i++;
      } else {
        // Chord line with lyrics - use the lyric line, skip chord line
        result.push(nextLine);
        i = nextLineIndex + 1;
      }
    } else {
      // Regular line - remove inline chords [C] format
      const cleanedLine = currentLine.replace(/\[([^\]]+)\]/g, '');
      if (cleanedLine.trim()) {
        result.push(cleanedLine);
      }
      i++;
    }
  }
  
  return result.join('\n');
}

/**
 * Interface for PDF-generering fra sang-data
 */
export interface GenerateSongPDFOptions {
  /** Sangtittel */
  title: string;
  /** Artist */
  artist?: string;
  /** Tekst og akkorder (raw string) */
  tekstOgAkkorder: string;
  /** Antall semitoner å transponere (0 = original) */
  semitones: number;
  /** Original grunntone (f.eks. 'C') */
  originalGrunntone?: string;
  /** Original toneart type (f.eks. 'dur' eller 'moll') */
  originalToneartType?: string;
  /** Fontstørrelse for tekst (default: 12) */
  fontSize?: number;
  /** Fontstørrelse for akkorder (default: 14) */
  chordFontSize?: number;
  /** Farge for akkorder (hex, default: '#2563eb') */
  chordColor?: string;
  /** Antall kolonner (default: 1) */
  columns?: number;
  /** Kun tekst (uten akkorder) - default: false */
  textOnly?: boolean;
}

/**
 * Generer PDF for en sang med gitt toneart
 * 
 * @param options - Konfigurasjon for PDF-generering
 */
export function generateSongPDF(options: GenerateSongPDFOptions): void {
  const {
    title,
    artist = 'Ukjent artist',
    tekstOgAkkorder,
    semitones = 0,
    originalGrunntone = 'C',
    originalToneartType = 'dur',
    fontSize = 12,
    chordFontSize = 14,
    chordColor = '#2563eb',
    columns = 1,
    textOnly = false,
  } = options;

  if (!tekstOgAkkorder) {
    throw new Error('Ingen tekst å laste ned');
  }

  try {
    let parsedLines: ParsedLine[];
    
    if (textOnly) {
      // Fjern akkorder fra tekst før parsing
      const textWithoutChords = removeChordsFromText(tekstOgAkkorder);
      // Parse som ren tekst (akkorder vil automatisk være tomme)
      parsedLines = parseText(textWithoutChords);
      // Fjern alle akkorder fra parsed lines (sikkerhetsnett)
      parsedLines = parsedLines.map(line => ({
        ...line,
        chords: []
      }));
    } else {
      // Parse tekst og akkorder
      parsedLines = parseText(tekstOgAkkorder);

      // Transpose chords if needed
      parsedLines = parsedLines.map(line => {
        if (line.chords.length === 0) return line;
        
        return {
          ...line,
          chords: line.chords.map(chord => ({
            ...chord,
            chord: transposeChord(chord.chord, semitones)
          }))
        };
      });
    }

    // Calculate transposed key
    const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const originalIndex = notes.indexOf(originalGrunntone);
    const newIndex = (originalIndex + semitones + 12) % 12;
    const transposedKey = semitones === 0 
      ? `${originalGrunntone} ${originalToneartType}` 
      : `${notes[newIndex]} ${originalToneartType} (${semitones > 0 ? '+' : ''}${semitones})`;

    // Generate PDF
    generateLeadsheetPDF(parsedLines, {
      title,
      artist,
      fontSize,
      chordFontSize,
      chordColor,
      columns,
      transposedKey,
      textOnly
    });
  } catch (err) {
    console.error('Kunne ikke generere PDF:', err);
    throw err;
  }
}

